/**
 * Loan Recommendation DTO — Astor's recommendation banner on Loan Detail.
 *
 * Renders as the big teal banner at the top of the Loan Detail screen:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Astor recommends: Approve at $725,000 with experience…      │
 *   │ Why: …reasoning prose, may end with "Show full reasoning →" │
 *   │ [credit.pull DONE] [comps.auto_pull READY] [strict_avm …]   │
 *   │                              [Approve & advance →] [Review] │
 *   │                            conf 82% · reasoned 7s ago       │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Wave 1 — DTO contract only. Wave 2 (8-agent-service) emits these from
 * Astor's reasoning pipeline; Wave 3 (10-business-portal `LoanDetail`)
 * renders the banner.
 */

import { z } from 'zod';

// ============================================================================
// Mode + chip-status enums
// ============================================================================

/**
 * What Astor is recommending. Drives the banner color + the primary CTA:
 *
 *   - `APPROVE`                  — green-teal banner, "Approve & advance" CTA
 *   - `APPROVE_WITH_EXCEPTION`   — teal banner, "Approve with exception" CTA
 *   - `REVIEW`                   — amber banner, "Open memo" CTA
 *   - `DENY`                     — red banner, "Open denial reasons" CTA
 *   - `PENDING`                  — gray banner, shimmer; recommendation not ready
 */
export const LoanRecommendationModeSchema = z.enum(['APPROVE', 'APPROVE_WITH_EXCEPTION', 'REVIEW', 'DENY', 'PENDING']);
export type LoanRecommendationMode = z.infer<typeof LoanRecommendationModeSchema>;

/**
 * Per-tool-call state for the chip row below the headline. Each tool Astor
 * called surfaces as a chip with this status driving the color + dot:
 *
 *   - `DONE`    — green chip, info populated
 *   - `READY`   — teal chip, output cached and ready to view
 *   - `PENDING` — gray chip with shimmer
 *   - `FAILED`  — red chip, hover surfaces error
 */
export const ToolCallStatusSchema = z.enum(['DONE', 'READY', 'PENDING', 'FAILED']);
export type ToolCallStatus = z.infer<typeof ToolCallStatusSchema>;

// ============================================================================
// Tool-call chip
// ============================================================================

/**
 * A single tool call that informed the recommendation. Rendered as a
 * `ToolCallChip` in the banner. `name` is the canonical tool key
 * (snake.dot.case) — `meta` is the human-readable hover label.
 */
export const ToolCallSummarySchema = z.object({
  id: z.string().uuid(),
  /**
   * Tool name (e.g. `credit.pull`, `comps.auto_pull`). snake.dot.case so
   * it can also act as a key in audit/log indexes. Max 60 chars.
   */
  name: z.string().min(1).max(60),
  status: ToolCallStatusSchema,
  /** Human label for hover/tooltip (e.g. "Pulled FICO from Experian, 2026-03-14"). */
  meta: z.string().optional(),
  /** Link to the underlying tool run (for click-through to the agent run inspector). */
  runId: z.string().uuid().optional(),
});
export type ToolCallSummaryDto = z.infer<typeof ToolCallSummarySchema>;

// ============================================================================
// Action button
// ============================================================================

/**
 * An action button rendered to the right of the banner. The UI maps
 * `actionRef` to a tRPC mutation or gRPC method when the user clicks.
 */
export const LoanRecommendationActionSchema = z.object({
  id: z.string().uuid(),
  /**
   * Label shown on the button (e.g. "Approve & advance →", "Review memo",
   * "Override"). Max 80 chars to keep banner readable.
   */
  label: z.string().min(1).max(80),
  /**
   * Visual variant:
   *   - `primary`   — teal CTA, used for the main forward action
   *   - `secondary` — outlined, used for "Review memo" / inspect flows
   *   - `link`      — text-only, used for "Override" / escape hatches
   */
  variant: z.enum(['primary', 'secondary', 'link']),
  /**
   * gRPC method or tRPC mutation name the UI should fire when clicked.
   * Optional because some actions are pure-UI (e.g. opening a side panel).
   */
  actionRef: z.string().optional(),
});
export type LoanRecommendationActionDto = z.infer<typeof LoanRecommendationActionSchema>;

// ============================================================================
// Root — loan recommendation
// ============================================================================

/**
 * The full recommendation envelope returned by the agent service for a
 * given case. One recommendation per case at a time — older recommendations
 * are versioned at the service layer, not surfaced through this DTO.
 */
export const LoanRecommendationSchema = z.object({
  caseId: z.string().uuid(),
  mode: LoanRecommendationModeSchema,
  /**
   * Confidence the recommendation is correct, 0-1. Maps to the "conf 82%"
   * text on the banner footer. Below ~0.6 the UI tints the chip warning.
   */
  confidence: z.number().min(0).max(1),
  /**
   * Single-line headline (e.g. "Approve at $725,000 with experience
   * exception"). Max 200 chars to fit on a single banner row.
   */
  headline: z.string().min(1).max(200),
  /**
   * One-paragraph rationale prose. May end with a "Show full reasoning →"
   * link in UI that opens the memo's reasoning callouts panel.
   */
  reasoning: z.string().min(1),
  /** Tool runs that informed this recommendation. */
  toolCalls: z.array(ToolCallSummarySchema).default([]),
  /** Action buttons shown to the right of the banner. */
  actions: z.array(LoanRecommendationActionSchema).default([]),
  /**
   * ISO timestamp of when this recommendation was generated. UI shows
   * "reasoned 7s ago" derived from this.
   */
  generatedAt: z.string().datetime(),
});
export type LoanRecommendationDto = z.infer<typeof LoanRecommendationSchema>;
